import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useModuleAccess } from '../Guards/PermissionGuard';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Users2,
  CreditCard,
  Wallet,
  PiggyBank,
  Coins,
  HandCoins,
  ChevronRight,
  Shield,
  X,
  User,
  FileText,
  BarChart3,
  Receipt,
  WalletCards,
  Table2,
  PlayCircle,
  StopCircle,
  Briefcase,
  Club   // ✅ SAFE replacement for Cards
} from 'lucide-react';
import logo from '../../assets/logo.png';

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const canAccessDashboard = useModuleAccess('dashboard');
  const canAccessMembership = useModuleAccess('membership');
  const canAccessSubscription = useModuleAccess('subscription');
  const canAccessCashCounter = useModuleAccess('cashCounter');
  const canAccessPettyCash = useModuleAccess('pettyCash');
  const canAccessCoins = useModuleAccess('coins');
  const canAccessKitty = useModuleAccess('kitty');
  const canAccessSettlement = useModuleAccess('settlement');

  useEffect(() => {
    if (window.innerWidth < 1024 && isOpen) onToggle();
  }, [location.pathname]);

  const isActive = path =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isSubActive = path =>
    location.pathname + location.search === path;

  const menuConfig = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', show: canAccessDashboard },

    {
      name: 'Membership',
      icon: Users,
      path: '/members',
      show: canAccessMembership,
      submenu: [
        { name: 'Create Members', path: '/members/add', icon: UserPlus },
        { name: 'Members List', path: '/members', icon: Users },
        
      ]
    },

    {
      name: 'Subscription',
      icon: CreditCard,
      path: '/subscription',
      show: canAccessSubscription,
      submenu: [
        { name: 'Subscription List', path: '/subscription', icon: BarChart3 },
        { name: 'Payment Entry', path: '/subscription/entry', icon: Receipt }
      ]
    },

    {
      name: 'Cash Counter',
      icon: Wallet,
      path: '/cash-counter',
      show: canAccessCashCounter,
      submenu: [
        { name: 'Transaction Entry', path: '/cash-counter', icon: WalletCards },
        { name: 'Members Balance', path: '/cash-counter/balance', icon: Users },
        { name: 'Reports', path: '/cash-counter/reports', icon: FileText }
      ]
    },
    {
      name: 'Kitty Rummy',
      icon: Club, 
      path: '/kitty',
      show: canAccessKitty,
      submenu: [
        { name: 'Dashboard', path: '/kitty', icon: LayoutDashboard },
        { name: 'Create Table', path: '/kitty/create', icon: Table2 },
        { name: 'Live Tables', path: '/kitty/live', icon: PlayCircle },
        { name: 'Closed Tables', path: '/kitty/closed', icon: StopCircle },
        { name: 'Players', path: '/kitty/players', icon: Users2 },
        { name: 'Staff', path: '/kitty/staff', icon: Briefcase },
      ]
    },
    {
      name: 'Petty Cash',
      icon: PiggyBank,
      path: '/petty-cash',
      show: canAccessPettyCash,
      submenu: [
        { name: 'Dashboard', path: '/petty-cash', icon: LayoutDashboard },
        { name: 'Expense Entry', path: '/petty-cash/expenses', icon: Receipt },
        { name: 'Salary Entry', path: '/petty-cash/salaries', icon: Wallet },
        { name: 'Reports', path: '/petty-cash/reports', icon: FileText }
      ]
    },

    { name: 'Coin Management', icon: Coins, path: '/coins', show: canAccessCoins },


    { name: 'Settlement', icon: Coins, path: '/settlement', show: canAccessSettlement },
    
  ];

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onToggle} />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-slate-200
        transition-transform duration-300 flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* LOGO */}
        <div className="h-16 px-4 flex items-center justify-between border-b border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white rounded-lg overflow-hidden">
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-sm font-semibold">Wilson Garden</span>
          </Link>
          <button onClick={onToggle} className="lg:hidden text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* USER */}
        <div className="px-4 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
              <User size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* MENU */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 scrollbar-none">
          {menuConfig.filter(m => m.show).map(item => {
            const Icon = item.icon;
            return (
              <div key={item.name}>
                {item.submenu ? (
                  <>
                    <button
                      onClick={() =>
                        setOpenSubmenu(openSubmenu === item.name ? null : item.name)
                      }
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium
                      ${isActive(item.path)
                        ? 'bg-slate-700 text-white'
                        : 'hover:bg-slate-800'}`}
                    >
                      <span className="flex items-center gap-3">
                        <Icon size={18} />
                        {item.name}
                      </span>
                      <ChevronRight
                        size={16}
                        className={`transition-transform ${
                          openSubmenu === item.name ? 'rotate-90' : ''
                        }`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all ${
                        openSubmenu === item.name ? 'max-h-96 mt-1' : 'max-h-0'
                      }`}
                    >
                      <ul className="ml-6 space-y-1 py-1">
                        {item.submenu.map(sub => {
                          const SubIcon = sub.icon;
                          return (
                            <li key={sub.path}>
                              <Link
                                to={sub.path}
                                className={`flex items-center gap-2 px-3 py-2 text-xs rounded-lg
                                ${
                                  isSubActive(sub.path)
                                    ? 'bg-indigo-500/20 text-indigo-300'
                                    : 'hover:bg-slate-800'
                                }`}
                              >
                                <SubIcon size={14} />
                                {sub.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    ${isActive(item.path)
                      ? 'bg-slate-700 text-white'
                      : 'hover:bg-slate-800'}`}
                  >
                    <Icon size={18} />
                    {item.name}
                  </Link>
                )}
              </div>
            );
          })}

          {user?.is_admin && (
            <Link
              to="/admin"
              className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
              bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Shield size={18} />
              Admin Panel
            </Link>
          )}
        </nav>
      </aside>

      <style>{`
        .scrollbar-none::-webkit-scrollbar { display: none; }
        .scrollbar-none { scrollbar-width: none; }
      `}</style>
    </>
  );
};

export default Sidebar;
